import { Title } from '@solidjs/meta'
import { useParams } from '@solidjs/router'
import { createEffect, createMemo, createResource, createSignal, For, onCleanup, onMount, Show } from 'solid-js'
import { createStore, produce } from 'solid-js/store'
import { untrack } from 'solid-js/web'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Drawer from '~/components/Drawer'
import Icon from '~/components/Icon'
import Modal from '~/components/Modal'
import Textarea from '~/components/Textarea'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useIsMobile } from '~/hooks/useIsMobile'
import { ExecutionDetail, ExecutionPanel } from '~/layers/ui/admin/automatizaciones/ExecutionPanel'
import { NodeInspector } from '~/layers/ui/admin/automatizaciones/NodeInspector'
import { NodePalette } from '~/layers/ui/admin/automatizaciones/NodePalette'
import { WorkflowCanvas } from '~/layers/ui/admin/automatizaciones/WorkflowCanvas'
import { workflowService } from '~/services/workflow.service'
import type { ValidationReportType, WorkflowExecutionDetailType, WorkflowGraphType, WorkflowNodeExecutionType, WorkflowStatsType } from '~/types/workflow'
import { resolveError } from '~/utils/error'

const emptyGraph = (): WorkflowGraphType => ({ schemaVersion: 1, nodes: [], edges: [] })

// structuredClone falla con el proxy de un store de Solid (DataCloneError) y
// silenciaba el guardado: el grafo se serializa con JSON en su lugar.
const cloneGraph = (g: WorkflowGraphType): WorkflowGraphType => JSON.parse(JSON.stringify(g)) as WorkflowGraphType

const genId = (p: string) => `${p}_${Math.random().toString(36).slice(2, 7)}`

/**
 * Editor de workflows.
 *
 * Separa la DEFINICIÓN (el grafo persistido) del ESTADO DEL EDITOR (selección,
 * zoom, pan, conexión en curso, historial…): el primero vive en un store y es lo
 * único que se guarda; el segundo son señales efímeras que no entran en el modelo.
 */
const WorkflowEditorPage = () => {
  const params = useParams<{ id: string }>()
  const toast = useToast()
  const alert = useAlert()
  const isMobile = useIsMobile()
  const isCompact = () => window.innerWidth < 1024

  // ── Definición (modelo persistido) ──────────────────────────────────────
  const [graph, setGraph] = createStore<WorkflowGraphType>(emptyGraph())
  const [revision, setRevision] = createSignal(1)
  const [wfName, setWfName] = createSignal('')
  const [dirty, setDirty] = createSignal(false)
  const [isSaving, setIsSaving] = createSignal(false)
  const [saveState, setSaveState] = createSignal<'saved' | 'dirty' | 'saving'>('saved')

  // ── Estado del editor (efímero) ─────────────────────────────────────────
  const [selected, setSelected] = createSignal<string | null>(null)
  const [selectedEdge, setSelectedEdge] = createSignal<string | null>(null)
  const [zoom, setZoom] = createSignal(1)
  const [pan, setPan] = createSignal({ x: 0, y: 0 })
  const [connectFrom, setConnectFrom] = createSignal<{ id: string; port: string } | null>(null)
  const [showPalette, setShowPalette] = createSignal(false)
  const [showInspector, setShowInspector] = createSignal(false)
  const [showTest, setShowTest] = createSignal(false)
  const [showExecutions, setShowExecutions] = createSignal(false)
  const [selectedExecution, setSelectedExecution] = createSignal<WorkflowExecutionDetailType | null>(null)
  const [testInput, setTestInput] = createSignal('{}')
  const [testResult, setTestResult] = createSignal<unknown>(null)
  const [testRunning, setTestRunning] = createSignal(false)
  const [validation, setValidation] = createSignal<ValidationReportType | null>(null)
  const [nodeSearch, setNodeSearch] = createSignal('')
  const [nodeCategory, setNodeCategory] = createSignal<string>('all')
  const [historyPast, setHistoryPast] = createSignal<WorkflowGraphType[]>([])
  const [historyFuture, setHistoryFuture] = createSignal<WorkflowGraphType[]>([])
  const [recentTypes, setRecentTypes] = createSignal<string[]>([])
  const [clipboard, setClipboard] = createSignal<WorkflowGraphType['nodes']>([])

  // ── Recursos ────────────────────────────────────────────────────────────
  const [workflow, { refetch: refetchWorkflow }] = createResource(
    () => params.id,
    async id => workflowService.get(id)
  )
  const [draft] = createResource(
    () => params.id,
    async id => workflowService.getDraft(Number(id))
  )
  const [nodeTypes] = createResource(async () => workflowService.nodeTypes())
  const [executions, { refetch: refetchExecutions }] = createResource(
    () => params.id,
    async id => workflowService.executions(Number(id), { limit: 20 }).catch(() => ({ items: [], meta: { total: 0 } }))
  )
  const [stats] = createResource<WorkflowStatsType | null, string>(
    () => params.id,
    async id => workflowService.stats(Number(id), 30).catch(() => null)
  )

  // ── Hidratación del borrador ────────────────────────────────────────────
  createEffect(() => {
    const d = draft()
    if (!d) return
    // No pisar cambios del usuario si el draft llega tarde: solo se hidrata si el canvas está vacío.
    if (graph.nodes.length === 0 && graph.edges.length === 0) {
      setGraph(d.graph ?? emptyGraph())
      setRevision(d.revision)
      setDirty(false)
      setSaveState('saved')
    }
  })

  createEffect(() => {
    const w = workflow()
    if (!w) return
    untrack(() => setWfName(w.name))
  })

  // ── Atajos de teclado y protección de cambios ───────────────────────────
  const undo = () => {
    const previous = historyPast().at(-1)
    if (!previous) return
    setHistoryPast(past => past.slice(0, -1))
    setHistoryFuture(future => [cloneGraph(graph), ...future].slice(0, 50))
    setGraph(previous)
    setDirty(true)
    setSaveState('dirty')
  }

  const redo = () => {
    const next = historyFuture()[0]
    if (!next) return
    setHistoryFuture(future => future.slice(1))
    setHistoryPast(past => [...past, cloneGraph(graph)].slice(-50))
    setGraph(next)
    setDirty(true)
    setSaveState('dirty')
  }

  const copyNode = (id: string) => {
    const n = graph.nodes.find(x => x.id === id)
    if (!n) return
    setClipboard([{ ...n }])
    toast.addToast({ title: 'Nodo copiado', type: 'success' })
  }

  const pasteNode = () => {
    const nodes = clipboard()
    if (!nodes.length) return
    const copy = nodes.map(n => ({ ...n, id: genId('n'), position: { x: n.position.x + 24, y: n.position.y + 24 } }))
    updateGraph(g => {
      for (const n of copy) g.nodes.push(n)
    })
    setSelected(copy[0].id)
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null
    const isEditing = target?.matches('input, textarea, select, [contenteditable="true"]')
    if (isEditing) return
    const modifier = event.ctrlKey || event.metaKey
    const key = event.key.toLowerCase()
    if (modifier && key === 'z') {
      event.preventDefault()
      if (event.shiftKey) redo()
      else undo()
    } else if (modifier && key === 'y') {
      event.preventDefault()
      redo()
    } else if (modifier && key === 'c') {
      if (selected()) {
        event.preventDefault()
        copyNode(selected()!)
      }
    } else if (modifier && key === 'v') {
      event.preventDefault()
      pasteNode()
    } else if (event.key === 'Delete') {
      if (selectedEdge()) {
        const edgeId = selectedEdge()
        updateGraph(g => {
          g.edges = g.edges.filter(e => e.id !== edgeId)
        })
        setSelectedEdge(null)
      } else if (selected()) {
        removeNode(selected()!)
      }
    }
  }

  onMount(() => {
    window.addEventListener('keydown', handleKeyDown)
    onCleanup(() => window.removeEventListener('keydown', handleKeyDown))
  })

  const guardUnsaved = (e: BeforeUnloadEvent) => {
    if (dirty()) {
      e.preventDefault()
      e.returnValue = ''
    }
  }
  onMount(() => {
    window.addEventListener('beforeunload', guardUnsaved)
    onCleanup(() => window.removeEventListener('beforeunload', guardUnsaved))
  })

  // ── Mutaciones del grafo con historial ──────────────────────────────────
  const updateGraph = (mutator: (draft: WorkflowGraphType) => void, recordHistory = true) => {
    if (recordHistory) {
      setHistoryPast(past => [...past.slice(-49), cloneGraph(graph)])
      setHistoryFuture([])
    }
    setGraph(produce(mutator))
    setDirty(true)
    setSaveState('dirty')
  }

  const selectedNode = createMemo(() => graph.nodes.find(n => n.id === selected()) ?? null)
  const selectedType = createMemo(() => {
    const n = selectedNode()
    if (!n) return null
    return nodeTypes()?.find(t => t.type === n.type) ?? null
  })

  const executionNodeStatus = createMemo(() => {
    const statuses = new Map<string, WorkflowNodeExecutionType>()
    for (const node of selectedExecution()?.nodes ?? []) statuses.set(node.nodeId, node)
    return statuses
  })

  // ── Acciones de nodos ───────────────────────────────────────────────────
  const addNode = (type: string) => {
    const def = nodeTypes()?.find(t => t.type === type)
    const id = genId('n')
    const count = graph.nodes.length
    const pos = isMobile() ? { x: 40, y: 40 + count * 150 } : { x: 160 + (count % 3) * 280, y: 120 + Math.floor(count / 3) * 160 }
    updateGraph(g => {
      g.nodes.push({ id, type, typeVersion: def?.version ?? 1, name: def?.label ?? type, config: {}, position: pos })
    })
    setSelected(id)
    setShowPalette(false)
    if (isCompact()) setShowInspector(true)
    // Registrar el tipo como reciente.
    setRecentTypes(recent => [type, ...recent.filter(t => t !== type)].slice(0, 8))
  }

  const removeNode = (id: string) => {
    updateGraph(g => {
      g.nodes = g.nodes.filter(n => n.id !== id)
      g.edges = g.edges.filter(e => e.from !== id && e.to !== id)
    })
    if (selected() === id) setSelected(null)
  }

  const duplicateNode = (id: string) => {
    const n = graph.nodes.find(x => x.id === id)
    if (!n) return
    const copy = { ...n, id: genId('n'), position: { x: n.position.x + 20, y: n.position.y + 20 }, name: `${n.name} copia` }
    updateGraph(g => g.nodes.push(copy))
    setSelected(copy.id)
  }

  const toggleDisableNode = (id: string) => {
    updateGraph(g => {
      const n = g.nodes.find(x => x.id === id)
      if (n) n.disabled = !n.disabled
    })
  }

  const connect = (toId: string) => {
    const source = connectFrom()
    if (!source || source.id === toId) {
      setConnectFrom(null)
      return
    }
    const fromId = source.id
    const fromNode = graph.nodes.find(n => n.id === fromId)
    const toNode = graph.nodes.find(n => n.id === toId)
    if (!fromNode || !toNode) {
      setConnectFrom(null)
      return
    }
    const toDef = nodeTypes()?.find(t => t.type === toNode.type)
    const fromPort = source.port
    const toPort = toDef?.ports.inputs[0]?.key ?? 'in'
    const fromPortLabel = nodeTypes()
      ?.find(t => t.type === fromNode.type)
      ?.ports.outputs.find(port => port.key === fromPort)?.label
    // Evitar duplicados
    if (graph.edges.some(e => e.from === fromId && e.to === toId && e.fromPort === fromPort)) {
      setConnectFrom(null)
      return
    }
    updateGraph(g => {
      g.edges.push({ id: genId('e'), from: fromId, fromPort, to: toId, toPort, label: fromPortLabel })
    })
    setConnectFrom(null)
  }

  const handleConnectFrom = (nodeId: string, port: string) => {
    const cur = connectFrom()
    setConnectFrom(cur?.id === nodeId && cur.port === port ? null : { id: nodeId, port })
  }

  const updateConfig = (key: string, value: unknown) => {
    const id = selected()
    if (!id) return
    updateGraph(g => {
      const n = g.nodes.find(x => x.id === id)
      if (n) n.config[key] = value
    })
  }

  const renameNode = (name: string) => {
    const id = selected()
    if (!id) return
    updateGraph(g => {
      const n = g.nodes.find(x => x.id === id)
      if (n) n.name = name
    })
  }

  // ── Arrastre de nodos (una sola operación de historial por arrastre) ────
  const onPointerDownNode = (e: PointerEvent, id: string) => {
    if (isMobile()) return
    let moved = false
    const start = { x: e.clientX, y: e.clientY }
    const node = graph.nodes.find(n => n.id === id)
    if (!node) return
    const orig = { ...node.position }
    const beforeDrag = cloneGraph(graph)
    const onMove = (ev: PointerEvent) => {
      const dx = (ev.clientX - start.x) / zoom()
      const dy = (ev.clientY - start.y) / zoom()
      if (Math.abs(dx) > 1 || Math.abs(dy) > 1) moved = true
      setGraph(
        produce(g => {
          const n = g.nodes.find(x => x.id === id)
          if (n) {
            n.position.x = orig.x + dx
            n.position.y = orig.y + dy
          }
        })
      )
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (!moved) return
      setHistoryPast(past => [...past.slice(-49), beforeDrag])
      setHistoryFuture([])
      setDirty(true)
      setSaveState('dirty')
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  // ── Guardado y publicación ──────────────────────────────────────────────
  const save = async (): Promise<boolean> => {
    setIsSaving(true)
    setSaveState('saving')
    try {
      const name = wfName().trim()
      if (name && name !== workflow()?.name) {
        await workflowService.update(Number(params.id), { name })
        void refetchWorkflow()
      }
      const res = await workflowService.saveDraft(Number(params.id), { graph: cloneGraph(graph), revision: revision() })
      setRevision(res.revision)
      setDirty(false)
      setSaveState('saved')
      toast.addToast({ title: 'Borrador guardado', type: 'success' })
      return true
    } catch (e) {
      const msg = resolveError(e)
      const esConflicto = typeof e === 'object' && e !== null && (e as { response?: { status?: number } }).response?.status === 409
      if (msg.includes('Recarga') || esConflicto) toast.addToast({ title: 'Conflicto: recarga el flujo', type: 'danger' })
      else toast.addToast({ title: msg, type: 'danger' })
      setSaveState('dirty')
      return false
    } finally {
      setIsSaving(false)
    }
  }

  const publish = async () => {
    try {
      if (!(await save())) return
      await workflowService.publish(Number(params.id))
      toast.addToast({ title: 'Publicado', type: 'success' })
      void refetchWorkflow()
      void refetchExecutions()
    } catch (e) {
      toast.addToast({ title: resolveError(e), type: 'danger' })
    }
  }

  const handleValidate = async () => {
    try {
      const report = await workflowService.validate(Number(params.id))
      setValidation(report)
      const c = (report.errors?.length ?? 0) + (report.warnings?.length ?? 0)
      toast.addToast({
        title: c ? `${report.errors.length} errores, ${report.warnings.length} avisos` : 'Sin errores',
        type: c ? (report.errors.length ? 'danger' : 'warning') : 'success'
      })
    } catch (e) {
      toast.addToast({ title: resolveError(e), type: 'danger' })
    }
  }

  const openExecution = async (publicId: string) => {
    try {
      const detail = await workflowService.execution(publicId)
      setSelectedExecution(detail)
      setShowExecutions(false)
    } catch (e) {
      toast.addToast({ title: resolveError(e), type: 'danger' })
    }
  }

  const runTest = async () => {
    let input: unknown = {}
    try {
      input = JSON.parse(testInput())
    } catch {
      toast.addToast({ title: 'JSON de entrada inválido', type: 'danger' })
      return
    }
    setTestRunning(true)
    try {
      const res = await workflowService.execute(Number(params.id), { input, mode: 'test' })
      setTestResult(res)
      toast.addToast({ title: res.result.status === 'succeeded' ? 'Test ok' : 'Test falló', type: res.result.status === 'succeeded' ? 'success' : 'danger' })
    } catch (e) {
      toast.addToast({ title: resolveError(e), type: 'danger' })
    } finally {
      setTestRunning(false)
    }
  }

  const availableVars = createMemo(() => {
    const cur = selected()
    if (!cur) return ['{{trigger.body}}', '{{trigger.headers}}', '{{vars.*}}', '{{execution.id}}'] as string[]
    const list = ['{{trigger.body}}', '{{trigger.body.email}}', '{{trigger.body.name}}', '{{vars.*}}', '{{execution.id}}', '{{execution.mode}}']
    for (const n of graph.nodes) if (n.id !== cur) list.push(`{{nodes.${n.id}.output}}`)
    return list
  })

  const openConfig = (id: string) => {
    setSelected(id)
    if (isCompact()) setShowInspector(true)
  }

  const goBackSafe = async () => {
    if (dirty()) {
      const ok = await alert.confirm({
        title: 'Cambios sin guardar',
        message: 'Tienes cambios sin guardar en este flujo. Si sales, se perderán.',
        confirmText: 'Salir sin guardar',
        cancelText: 'Quedarme',
        type: 'warning'
      })
      if (!ok) return
    }
    if (window.history.length > 1) window.history.back()
    else window.location.href = '/admin/automatizaciones'
  }

  const toggleStatus = async () => {
    const w = workflow()
    if (!w) return
    try {
      if (w.status === 'active') await workflowService.deactivate(Number(params.id))
      else await workflowService.activate(Number(params.id))
      void refetchWorkflow()
      toast.addToast({ title: w.status === 'active' ? 'Flujo desactivado' : 'Flujo activado', type: 'success' })
    } catch (e) {
      toast.addToast({ title: resolveError(e), type: 'danger' })
    }
  }

  const validationCount = createMemo(() => (validation()?.errors.length ?? 0) + (validation()?.warnings.length ?? 0))
  const validationErrors = createMemo(() => validation()?.errors.length ?? 0)

  return (
    <>
      <Title>{workflow()?.name ?? 'Editor'} | Automatizaciones</Title>
      <div class="flex h-[calc(100dvh-4rem)] flex-col" data-testid="workflow-editor">
        {/* Header */}
        <div class="flex flex-wrap items-center gap-2 border-b border-border bg-surface px-3 py-2 shadow-sm">
          <button type="button" onClick={() => void goBackSafe()} class="group flex items-center gap-1 text-sm font-medium text-foreground-muted hover:text-primary">
            <span class="flex h-8 w-8 items-center justify-center rounded-full bg-surface-muted transition-colors group-hover:bg-primary-100 group-hover:text-primary">
              <Icon icon="material-symbols-light:arrow-back" size={18} />
            </span>
            <span class="hidden sm:inline">Workflows</span>
          </button>
          <div class="min-w-0 flex-1">
            <input
              aria-label="Nombre del flujo"
              class="w-full min-w-0 truncate rounded bg-transparent font-semibold text-foreground outline-none focus:ring-2 focus:ring-primary-500"
              value={wfName()}
              onInput={e => {
                setWfName((e.currentTarget as HTMLInputElement).value)
                setDirty(true)
                setSaveState('dirty')
              }}
            />
            <p class="flex items-center gap-1.5 text-xs text-foreground-subtle truncate">
              /{workflow()?.slug ?? ''} · rev {revision()}
              <span data-testid="save-indicator" class={`inline-flex items-center gap-1 ${saveState() === 'saving' ? 'text-info' : saveState() === 'dirty' ? 'text-warning' : 'text-success'}`}>
                <Icon
                  icon={
                    saveState() === 'saving' ? 'material-symbols-light:sync' : saveState() === 'dirty' ? 'material-symbols-light:cloud-upload-outline' : 'material-symbols-light:cloud-done-outline'
                  }
                  size={13}
                />
                {saveState() === 'saving' ? 'Guardando…' : saveState() === 'dirty' ? 'Sin guardar' : 'Guardado'}
              </span>
            </p>
          </div>
          <div class="flex items-center gap-1">
            <span
              class={`hidden rounded-full px-2 py-1 text-[11px] font-medium sm:inline-flex ${workflow()?.status === 'active' ? 'bg-success-soft text-success' : workflow()?.status === 'archived' ? 'bg-surface-container text-foreground-subtle' : 'bg-warning-soft text-warning'}`}
            >
              {workflow()?.status === 'active' ? 'Activo' : workflow()?.status === 'archived' ? 'Archivado' : 'Borrador'}
            </span>
            <ButtonGroup size="sm" variant="soft" color="secondary">
              <Button icon="material-symbols-light:undo" onClick={undo} disabled={!historyPast().length} tooltip="Deshacer (Ctrl/Cmd + Z)" />
              <Button icon="material-symbols-light:redo" onClick={redo} disabled={!historyFuture().length} tooltip="Rehacer (Ctrl/Cmd + Shift + Z)" />
              <Button icon="material-symbols-light:verified-outline" onClick={handleValidate} tooltip={validationCount() ? `${validationCount()} aviso(s) de validación` : 'Validar'} />
              <Button
                icon="material-symbols-light:history"
                onClick={() => {
                  setShowExecutions(true)
                  void refetchExecutions()
                }}
                tooltip="Historial"
              />
            </ButtonGroup>
            <Show when={validationCount() > 0}>
              <span data-testid="validation-count" class={`rounded-full px-2 py-0.5 text-[10px] font-bold ${validationErrors() ? 'bg-danger-soft text-danger' : 'bg-warning-soft text-warning'}`}>
                {validationCount()}
              </span>
            </Show>
            <Button size="sm" variant="soft" color="info" onClick={() => setShowTest(true)} icon="material-symbols-light:play-arrow">
              Probar
            </Button>
            <Button size="sm" variant="soft" color="warning" disabled={isSaving()} onClick={save} icon="material-symbols-light:save-outline">
              Guardar
            </Button>
            <Show when={workflow()?.status === 'active'}>
              <Button size="sm" variant="soft" color="danger" icon="material-symbols-light:power-settings-new" onClick={toggleStatus}>
                Desactivar
              </Button>
            </Show>
            <Show when={workflow()?.status === 'inactive'}>
              <Button size="sm" variant="soft" color="success" icon="material-symbols-light:power" onClick={toggleStatus}>
                Activar
              </Button>
            </Show>
            <Button
              size="sm"
              color="primary"
              onClick={publish}
              icon="material-symbols-light:rocket-launch-outline"
              disabled={validationErrors() > 0}
              tooltip={validationErrors() ? 'Corrige los errores de validación antes de publicar' : 'Publicar'}
            >
              Publicar
            </Button>
          </div>
        </div>

        {/* Cuerpo: palette + canvas + inspector */}
        <div class="flex min-h-0 flex-1">
          <aside class="hidden w-64 shrink-0 overflow-y-auto border-r border-border bg-surface p-3 lg:block">
            <div class="mb-2 flex items-center justify-between">
              <h3 class="text-xs font-semibold uppercase text-foreground-subtle">Nodos</h3>
            </div>
            <NodePalette
              nodeTypes={nodeTypes() ?? []}
              search={nodeSearch()}
              onSearch={setNodeSearch}
              category={nodeCategory()}
              onCategory={setNodeCategory}
              onAdd={addNode}
              recentTypes={recentTypes()}
            />
            <div class="mt-4 rounded-lg bg-surface-muted p-2.5 text-[11px] leading-relaxed text-foreground-subtle">
              <p>• Clic en un puerto de salida y luego en el nodo destino para conectar.</p>
              <p>• Arrastra un nodo para posicionarlo. Arrastra el fondo para mover la vista.</p>
              <p>• Rueda para acercar/alejar. Clic en una conexión y Supr para eliminarla.</p>
            </div>
          </aside>

          <WorkflowCanvas
            graph={graph}
            nodeTypes={nodeTypes() ?? []}
            nodeExecutions={executionNodeStatus()}
            validation={validation()}
            zoom={zoom()}
            onZoom={setZoom}
            pan={pan()}
            onPan={setPan}
            selected={selected()}
            onSelect={setSelected}
            selectedEdge={selectedEdge()}
            onSelectEdge={setSelectedEdge}
            connectFrom={connectFrom()}
            onConnectFrom={handleConnectFrom}
            onConnectComplete={connect}
            onUpdateGraph={updateGraph}
            onDuplicateNode={duplicateNode}
            onCopyNode={copyNode}
            onToggleDisableNode={toggleDisableNode}
            onRemoveNode={removeNode}
            onOpenConfig={openConfig}
            onPointerDownNode={onPointerDownNode}
            onAddNode={() => setShowPalette(true)}
          />

          <aside class="hidden w-80 shrink-0 overflow-y-auto border-l border-border bg-surface p-4 lg:block">
            <div class="mb-3 flex items-center justify-between">
              <h2 class="text-sm font-semibold text-foreground">{selectedNode()?.name ?? 'Propiedades'}</h2>
              <Show when={selectedNode()}>
                <Button size="xs" variant="text" color="secondary" icon="material-symbols-light:close" aria-label="Cerrar propiedades" onClick={() => setSelected(null)} />
              </Show>
            </div>
            <NodeInspector
              node={selectedNode()}
              def={selectedType()}
              onConfig={updateConfig}
              onName={renameNode}
              onDuplicate={duplicateNode}
              onRemove={removeNode}
              onToggleDisable={toggleDisableNode}
              availableVars={availableVars()}
            />
          </aside>
        </div>

        {/* Barra inferior móvil */}
        <div class="flex items-center justify-around border-t border-border bg-surface p-2 lg:hidden">
          <Button size="sm" variant="soft" onClick={() => setShowPalette(true)} icon="material-symbols-light:widgets-outline">
            Nodos
          </Button>
          <Button size="sm" variant="soft" onClick={() => setShowInspector(true)} icon="material-symbols-light:tune">
            Propiedades
          </Button>
          <Button size="sm" variant="soft" onClick={() => setShowTest(true)} icon="material-symbols-light:play-arrow">
            Probar
          </Button>
        </div>
      </div>

      {/* Drawer de nodos (móvil) */}
      <Drawer isOpen={showPalette()} onClose={() => setShowPalette(false)} position="left" size="sm">
        <div class="p-4 space-y-3">
          <h2 class="font-semibold">Añadir nodo</h2>
          <NodePalette
            nodeTypes={nodeTypes() ?? []}
            search={nodeSearch()}
            onSearch={setNodeSearch}
            category={nodeCategory()}
            onCategory={setNodeCategory}
            onAdd={addNode}
            recentTypes={recentTypes()}
          />
        </div>
      </Drawer>

      {/* Drawer de propiedades (móvil, hoja inferior) */}
      <Drawer isOpen={showInspector()} onClose={() => setShowInspector(false)} position="bottom" size="md">
        <div class="space-y-4 p-4" data-testid="workflow-inspector-drawer">
          <div class="flex items-center justify-between border-b border-border pb-3">
            <div>
              <p class="text-xs font-medium uppercase tracking-wide text-primary">Configuración</p>
              <h2 class="font-semibold">{selectedNode()?.name ?? 'Propiedades'}</h2>
            </div>
            <Button size="sm" variant="text" icon="material-symbols-light:close" aria-label="Cerrar propiedades" onClick={() => setShowInspector(false)} />
          </div>
          <NodeInspector
            node={selectedNode()}
            def={selectedType()}
            onConfig={updateConfig}
            onName={renameNode}
            onDuplicate={duplicateNode}
            onRemove={removeNode}
            onToggleDisable={toggleDisableNode}
            availableVars={availableVars()}
          />
          <div class="sticky bottom-0 -mx-4 border-t border-border bg-surface p-4">
            <Button color="primary" fullWidth disabled={isSaving()} onClick={save} icon="material-symbols-light:save-outline">
              {isSaving() ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Drawer de historial */}
      <Drawer isOpen={showExecutions()} onClose={() => setShowExecutions(false)} position="right" size="sm">
        <div class="p-4 space-y-3">
          <h2 class="flex items-center justify-between font-semibold">
            Historial
            <Button size="xs" variant="text" onClick={() => void refetchExecutions()} icon="material-symbols-light:refresh" aria-label="Actualizar historial" />
          </h2>
          <Show when={validation()}>
            <div class="rounded-lg border p-2 text-xs max-h-40 overflow-auto bg-surface-container">
              <p class="font-semibold">Validación</p>
              <For each={validation()?.errors ?? []}>{e => <p class="text-danger">✗ {e.message}</p>}</For>
              <For each={validation()?.warnings ?? []}>{w => <p class="text-warning">⚠ {w.message}</p>}</For>
              <Show when={!validation()?.errors?.length && !validation()?.warnings?.length}>
                <p class="text-success">Sin errores</p>
              </Show>
            </div>
          </Show>
          <ExecutionPanel executions={executions()?.items ?? []} stats={stats() ?? null} loading={executions.loading} onRefresh={() => void refetchExecutions()} onOpen={openExecution} />
        </div>
      </Drawer>

      {/* Modal de prueba */}
      <Modal isOpen={showTest()} onClose={() => setShowTest(false)} size="lg">
        <Modal.Header>
          <h2 class="font-semibold">Probar flujo</h2>
          <p class="text-xs text-foreground-subtle">Se ejecuta en modo test (no escribe BD real para nodos sensibles)</p>
        </Modal.Header>
        <Modal.Body>
          <div class="space-y-3">
            <Textarea label="Input JSON (trigger.body)" value={testInput()} onInput={e => setTestInput((e.currentTarget as HTMLTextAreaElement).value)} rows={4} />
            <Show when={testResult()}>
              <pre class="bg-surface-container p-3 rounded text-xs overflow-auto max-h-64">{JSON.stringify(testResult(), null, 2)}</pre>
            </Show>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="soft" onClick={() => setShowTest(false)}>
              Cerrar
            </Button>
            <Button color="primary" onClick={runTest} loading={testRunning()} icon="material-symbols-light:play-arrow">
              Ejecutar test
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal de detalle de ejecución */}
      <Modal isOpen={!!selectedExecution()} onClose={() => setSelectedExecution(null)} size="lg">
        <Modal.Header>
          <h2 class="font-semibold">Ejecución {selectedExecution()?.execution?.publicId}</h2>
        </Modal.Header>
        <Modal.Body>
          <ExecutionDetail
            detail={selectedExecution()}
            onClose={() => setSelectedExecution(null)}
            onRetry={async publicId => {
              await workflowService.retryExecution(publicId)
              toast.addToast({ title: 'Reintento encolado', type: 'success' })
              setSelectedExecution(null)
            }}
            onCancel={async publicId => {
              await workflowService.cancelExecution(publicId)
              toast.addToast({ title: 'Cancel solicitado', type: 'success' })
            }}
          />
        </Modal.Body>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default WorkflowEditorPage
